import LogoutIcon from "@mui/icons-material/Logout";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Hospital, Lock, Palette, Settings } from "lucide-react";
import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import RheaLogo from "../assets/images/logo.svg";
import { useAuth } from "../contexts/AuthContext";
import { colors, fontSizes, sidebarWidth } from "../theme";
import { navigationItems, UserRole } from "../types";
import { getInitials, isRtlLanguage } from "../utils";
import SignoutDialog from "./dialogs/SignoutConfirmation";
import { useApp } from "../contexts/AppContext";
import { useApi } from "../contexts/ApiContext";

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
  cursor: "pointer",
  "&.MuiListItemButton-root": {
    margin: "0 auto",
    borderRadius: 12,
    width: "90%",
    color: colors.stone[950],

    "&:hover": {
      backgroundColor: colors.stone[50],
    },
  },
  "&.Mui-selected": {
    backgroundColor: `${colors.stone[100]} !important`,
    "& *": {
      color: colors.stone[950],
      fontWeight: 500,
    },
  },

  ".MuiListItemIcon-root": {
    minWidth: 40,
  },
}));

const ClinicSwitcher: React.FC = () => {
  const { clinics, user } = useApp();
  const { apiService } = useApi();

  if (user?.role !== UserRole.SuperAdmin || clinics.length === 0) {
    return null;
  }

  const preferred = apiService.getClinicOverride() ?? user?.clinic ?? "";
  const selected = clinics.some((clinic) => clinic.id === preferred)
    ? preferred
    : "";

  const handleChange = (clinicId: string) => {
    if (clinicId === apiService.getClinicOverride()) {
      return;
    }
    apiService.setClinicOverride(clinicId);
    // Reload so every context and page re-fetches against the selected clinic.
    window.location.reload();
  };

  return (
    <Box sx={{ px: 2, pb: 1 }}>
      <Typography variant="caption" color={colors.stone[500]}>
        Clinic (Super Admin)
      </Typography>
      <FormControl fullWidth size="small">
        <Select
          value={selected}
          onChange={(event) => handleChange(event.target.value as string)}
          sx={{ fontSize: fontSizes.sm }}
        >
          {clinics.map((clinic) => (
            <MenuItem
              key={clinic.id}
              value={clinic.id}
              sx={{ fontSize: fontSizes.sm }}
            >
              {clinic.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const Sidebar: React.FC = () => {
  const [isSignoutDialogOpen, setIsSignoutDialogOpen] = React.useState(false);

  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { translate, currentLanguage } = useApp();
  const { setSubmitting } = useApi();

  const resourceName = location.pathname.split("/")[1];

  const handleSignoutDialogClose = () => {
    setIsSignoutDialogOpen(false);
  };

  const handleSignOut = async () => {
    try {
      setSubmitting(true);
      await signOut();
    } catch (error: any) {
      console.error(error);
    } finally {
      setSubmitting(false);
      navigate("/login");
    }
  };

  const translations = useMemo(() => translate("general").sidebar, [translate]);

  console.log("sidebar user", user);

  return (
    <Drawer
      anchor={isRtlLanguage(currentLanguage) ? "right" : "left"}
      variant="permanent"
      sx={{
        width: sidebarWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: sidebarWidth,
          boxSizing: "border-box",
        },
      }}
    >
      <Stack
        direction="column"
        justifyContent="space-between"
        sx={{ height: "100%" }}
      >
        <Stack>
          <List>
            <ListItem key={"logo"}>
              <Stack gap={0}>
                <Box textAlign={"left"}>
                  <img width={"80px"} src={RheaLogo} />
                </Box>
                {user?.clinicName && (
                  <Typography pl={1} variant="caption" textAlign={"center"}>
                    {user.clinicName}
                  </Typography>
                )}
              </Stack>
            </ListItem>
          </List>
          <ClinicSwitcher />
          <List>
            {navigationItems.map((item, index) => (
              <Tooltip
                key={index}
                title={item.comingSoon ? translations.comingSoon : ""}
              >
                <div>
                  <StyledListItem
                    onClick={() => navigate(`/${item.path}`)}
                    disabled={item.comingSoon}
                    selected={resourceName === item.path}
                  >
                    <ListItemIcon>
                      {item.comingSoon ? <Lock /> : item.icon}
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        textAlign: isRtlLanguage(currentLanguage)
                          ? "right"
                          : "left",
                      }}
                      primaryTypographyProps={{ fontSize: fontSizes.sm }}
                    >
                      {translations[item.path]}{" "}
                    </ListItemText>
                  </StyledListItem>
                </div>
              </Tooltip>
            ))}
          </List>
        </Stack>
        <Stack>
          <List>
            {(user?.role === UserRole.Admin ||
              user?.role === UserRole.SuperAdmin) && (
              <StyledListItem
                selected={resourceName === "manage"}
                onClick={() => navigate(`/manage`)}
              >
                <ListItemIcon>
                  <Hospital />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    textAlign: isRtlLanguage(currentLanguage)
                      ? "right"
                      : "left",
                  }}
                  primaryTypographyProps={{
                    fontSize: fontSizes.sm,
                    variant: "navbar",
                  }}
                  primary={translations.manage}
                />
              </StyledListItem>
            )}
            <StyledListItem
              selected={resourceName === "report"}
              onClick={() => navigate(`/report`)}
            >
              <ListItemIcon>
                <Palette />
              </ListItemIcon>
              <ListItemText
                sx={{
                  textAlign: isRtlLanguage(currentLanguage) ? "right" : "left",
                }}
                primaryTypographyProps={{
                  fontSize: fontSizes.sm,
                  variant: "navbar",
                }}
                primary={translations.customize}
              />
            </StyledListItem>
            <StyledListItem
              onClick={() => navigate(`/settings`)}
              selected={resourceName === "settings"}
            >
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText
                sx={{
                  textAlign: isRtlLanguage(currentLanguage) ? "right" : "left",
                }}
                primaryTypographyProps={{
                  fontSize: fontSizes.sm,
                  variant: "navbar",
                }}
                primary={translations.settings}
              />
            </StyledListItem>
          </List>

          <Box sx={{ p: 2 }}>
            <Divider />
            <Stack alignItems={"center"} mt={2} gap={1} direction="row">
              <Link to={"/profile"}>
                <Avatar>{getInitials(user?.name ?? "")}</Avatar>
              </Link>
              <Stack>
                <Link to={"/profile"}>
                  <Typography fontSize={fontSizes.sm} variant="body1">
                    {user?.name}
                  </Typography>
                </Link>
                <Link to="/profile">
                  <Typography
                    fontSize={fontSizes.xs}
                    sx={{
                      whiteSpace: "nowrap",
                      maxWidth: "100px",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                    variant="body2"
                  >
                    {user?.email}
                  </Typography>
                </Link>
              </Stack>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton onClick={() => setIsSignoutDialogOpen(true)} sx={isRtlLanguage(currentLanguage) ? { transform: "rotateY(180deg)" } : {}}>
                <LogoutIcon  />
              </IconButton>
            </Stack>
          </Box>
        </Stack>
      </Stack>

      <SignoutDialog
        open={isSignoutDialogOpen}
        onClose={handleSignoutDialogClose}
        onConfirm={handleSignOut}
      />
    </Drawer>
  );
};

export default Sidebar;
